import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";
import { useQuote } from "@/lib/useQuote";
import { imgUrl } from "@/lib/api";
import { inr } from "@/lib/format";
import { btn } from "@/lib/ui";
import { QtySelector } from "./ProductBits";
import { Totals } from "./OrderLines";

const CartLine = ({ item, error }) => {
  const { setQty, remove } = useCart();
  return (
    <li className="flex gap-4 py-5" data-testid={`cart-line-${item.slug}`}>
      <img src={imgUrl(item.image)} alt="" className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/shop/${item.slug}`} className="font-semibold text-brand-ink hover:text-brand-blue">{item.name}</Link>
          <button type="button" onClick={() => remove(item.product_id)} aria-label={`Remove ${item.name}`} data-testid={`cart-remove-${item.slug}`} className="rounded-full p-2 text-slate-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-slate-600">{inr(item.price)} each</p>
        <div className="mt-2 flex items-center justify-between">
          <QtySelector value={item.qty} onChange={(q) => setQty(item.product_id, q)} max={Math.max(1, item.stock)} testid={`cart-qty-${item.slug}`} />
          <span className="font-semibold text-brand-ink">{inr(item.price * item.qty)}</span>
        </div>
        {error && <p className="mt-2 text-sm font-medium text-red-700" data-testid={`cart-error-${item.slug}`}>{error}</p>}
      </div>
    </li>
  );
};

function shippingHintText(quote, deliveryText) {
  if (quote.shipping === 0) return "You get free shipping.";
  const gap = quote.free_shipping_threshold - quote.subtotal;
  if (gap > 0) return `Add ${inr(gap)} more for free shipping.`;
  return deliveryText;
}

const ShippingHint = ({ quote }) => {
  const { settings } = useSite();
  return (
    <p className="flex items-center gap-2 rounded-xl bg-brand-greenTint px-3 py-2 text-sm text-brand-greenDeep" data-testid="cart-shipping-hint">
      <Truck className="h-4 w-4 shrink-0" aria-hidden="true" />
      {shippingHintText(quote, settings.delivery_text)}
    </p>
  );
};

const CartEmpty = ({ onShop }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center" data-testid="cart-empty">
    <ShoppingBag className="h-10 w-10 text-slate-400" aria-hidden="true" />
    <p className="text-base text-slate-700">Your cart is empty.</p>
    <Link to="/shop" onClick={onShop} className={btn("green", "md")} data-testid="cart-empty-shop-btn">Visit the shop</Link>
  </div>
);

const CartFooter = ({ quote, blocked, onCheckout }) => (
  <div className="space-y-4 border-t border-slate-200 bg-brand-tint px-6 py-5">
    {quote ? <Totals {...quote} /> : <p className="text-sm text-slate-600">Calculating totals…</p>}
    {quote && <ShippingHint quote={quote} />}
    <Link
      to="/checkout"
      onClick={(e) => (blocked ? e.preventDefault() : onCheckout())}
      aria-disabled={blocked}
      data-testid="cart-checkout-btn"
      className={`${btn("green", "lg")} w-full ${blocked ? "pointer-events-none opacity-50" : ""}`}
    >
      Proceed to checkout
    </Link>
  </div>
);

const quoteErrors = (quote) => Object.fromEntries((quote?.errors || []).map((e) => [e.product_id, e.message]));

export const CartDrawer = () => {
  const { items, open, setOpen, syncFromQuote } = useCart();
  const { quote, loading } = useQuote(open ? items : []);
  useEffect(() => {
    if (quote?.lines) syncFromQuote(quote.lines);
  }, [quote, syncFromQuote]);
  const errors = quoteErrors(quote);
  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="cart-drawer">
        <div className="border-b border-slate-100 px-6 py-5">
          <SheetTitle className="text-xl font-bold text-brand-ink">Your cart</SheetTitle>
          <SheetDescription className="text-sm text-slate-600">Delivery across India</SheetDescription>
        </div>
        {items.length === 0 && <CartEmpty onShop={close} />}
        {items.length > 0 && (
          <>
            <ul className="flex-1 divide-y divide-slate-100 overflow-y-auto px-6">
              {items.map((i) => <CartLine key={i.product_id} item={i} error={errors[i.product_id]} />)}
            </ul>
            <CartFooter quote={quote} blocked={loading || !quote || quote.errors.length > 0} onCheckout={close} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
